import React, { useState, useEffect } from 'react';
import { BookOpen, TrendingUp, CheckCircle, AlertCircle, Clock, Users, Search, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCurrentUser } from '@/data/users';

export default function SyllabusProgressPage() {
  const currentUser = getCurrentUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedTerm, setSelectedTerm] = useState('Term 1');
  const [syllabusData, setSyllabusData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setSyllabusData([
        { id: 1, subject: 'Mathematics', teacher: 'Joella Tamata', year: 9, term: 'Term 1', total_topics: 12, completed: 10, in_progress: 1, not_started: 1, percentage: 83 },
        { id: 2, subject: 'English', teacher: 'Johnny Still Tari', year: 10, term: 'Term 1', total_topics: 10, completed: 8, in_progress: 2, not_started: 0, percentage: 80 },
        { id: 3, subject: 'Basic Science', teacher: 'Rowen Tari', year: 9, term: 'Term 1', total_topics: 15, completed: 12, in_progress: 2, not_started: 1, percentage: 80 },
        { id: 4, subject: 'French', teacher: 'Nicholas Aga', year: 8, term: 'Term 1', total_topics: 14, completed: 14, in_progress: 0, not_started: 0, percentage: 100 },
        { id: 5, subject: 'Agriculture', teacher: 'Edward Banga', year: 10, term: 'Term 1', total_topics: 16, completed: 10, in_progress: 3, not_started: 3, percentage: 63 },
        { id: 6, subject: 'Social Science', teacher: 'Kathleen Tahi', year: 10, term: 'Term 1', total_topics: 8, completed: 6, in_progress: 1, not_started: 1, percentage: 75 },
        { id: 7, subject: 'Technology', teacher: 'Trevina Tari', year: 10, term: 'Term 1', total_topics: 10, completed: 4, in_progress: 2, not_started: 4, percentage: 40 },
        { id: 8, subject: 'Religious Education', teacher: 'Fr. Jacob Ute', year: 9, term: 'Term 1', total_topics: 12, completed: 5, in_progress: 3, not_started: 4, percentage: 42 },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusBadge = (percentage) => {
    if (percentage >= 80) return <Badge className="bg-green-100 text-green-800">On Track</Badge>;
    if (percentage >= 60) return <Badge className="bg-yellow-100 text-yellow-800">At Risk</Badge>;
    return <Badge className="bg-red-100 text-red-800">Behind</Badge>;
  };

  const filteredData = syllabusData.filter(item => {
    const matchesSearch = item.subject.toLowerCase().includes(searchTerm.toLowerCase()) || item.teacher.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesYear = selectedYear === 'all' || item.year.toString() === selectedYear;
    return matchesSearch && matchesYear;
  });

  const stats = {
    total: syllabusData.length,
    onTrack: syllabusData.filter(s => s.percentage >= 80).length,
    atRisk: syllabusData.filter(s => s.percentage >= 60 && s.percentage < 80).length,
    behind: syllabusData.filter(s => s.percentage < 60).length,
    avgProgress: Math.round(syllabusData.reduce((acc, s) => acc + s.percentage, 0) / syllabusData.length)
  };

  return (
    <div className="space-y-6" data-testid="syllabus-progress-page">
      <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-blue-500" />
            Syllabus Progress
          </h1>
          <p className="text-muted-foreground mt-1">Monitor curriculum coverage across all subjects and teachers</p>
        </div>
        <Select value={selectedTerm} onValueChange={setSelectedTerm}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Select term" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Term 1">Term 1</SelectItem>
            <SelectItem value="Term 2">Term 2</SelectItem>
            <SelectItem value="Term 3">Term 3</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-blue-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Total Subjects</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{stats.total}</div></CardContent></Card>
        <Card className="border-l-4 border-l-green-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">On Track (80%+)</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-green-600">{stats.onTrack}</div></CardContent></Card>
        <Card className="border-l-4 border-l-yellow-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">At Risk (60-79%)</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-yellow-600">{stats.atRisk}</div></CardContent></Card>
        <Card className="border-l-4 border-l-red-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Behind (&lt;60%)</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-red-600">{stats.behind}</div></CardContent></Card>
        <Card className="border-l-4 border-l-purple-500"><CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Avg Progress</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold text-purple-600">{stats.avgProgress}%</div></CardContent></Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by subject or teacher..." className="pl-9" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="search-input" />
            </div>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[150px]" data-testid="year-filter"><Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Filter by year" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {[7,8,9,10,11,12,13].map(y => <SelectItem key={y} value={y.toString()}>Year {y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Subject Progress</CardTitle><CardDescription>Syllabus completion status for {selectedTerm}</CardDescription></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground"><BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No syllabus data found</p></div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Subject</TableHead><TableHead>Teacher</TableHead><TableHead>Year</TableHead><TableHead>Topics</TableHead><TableHead>Progress</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredData.map((item) => (
                  <TableRow key={item.id} data-testid={`syllabus-${item.id}`}>
                    <TableCell className="font-medium">{item.subject}</TableCell>
                    <TableCell>{item.teacher}</TableCell>
                    <TableCell>Year {item.year}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-green-600">{item.completed}</span>/
                        <span className="text-yellow-600">{item.in_progress}</span>/
                        <span className="text-red-600">{item.not_started}</span>
                        <span className="text-muted-foreground">({item.total_topics} total)</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={item.percentage} className="w-20 h-2" />
                        <span className="text-sm font-medium">{item.percentage}%</span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(item.percentage)}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="sm">View Details</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardHeader><CardTitle className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-amber-600" />Subjects Requiring Attention</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredData.filter(s => s.percentage < 60).map(item => (
              <div key={item.id} className="bg-white rounded-lg p-4 border border-amber-200">
                <div className="flex justify-between items-start mb-2">
                  <div><h4 className="font-semibold">{item.subject} - Year {item.year}</h4><p className="text-sm text-muted-foreground">{item.teacher}</p></div>
                  <Badge className="bg-red-100 text-red-800">{item.percentage}%</Badge>
                </div>
                <Progress value={item.percentage} className="h-2 mb-2" />
                <p className="text-xs text-muted-foreground">{item.not_started} topics not started, {item.in_progress} in progress</p>
              </div>
            ))}
            {filteredData.filter(s => s.percentage < 60).length === 0 && (
              <div className="col-span-2 text-center py-4 text-green-600"><CheckCircle className="h-8 w-8 mx-auto mb-2" /><p>All subjects are on track or near completion!</p></div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
